import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Gift, Loader2, UserPlus } from 'lucide-react';
import { useAuth } from '../../../../src/contexts/AuthContext';
import { useToast } from '../../../../src/contexts/ToastContext';
import { podeGerirIndicacoes, podeUsarIndicacoes } from '../../../../src/lib/indicacoes/permissoes';
import { formatarDataHora, resumoPessoa } from '../../../../src/lib/indicacoes/rotulos';
import { indicacaoDaNegociacao, vincularEspontanea } from '../../../../src/services/indicacoes';
import type { IndicacaoDaNegociacao } from '../../../../types';
import ModalBase from '../../Indicacoes/ModalBase';
import PessoaPicker, { type PessoaEscolhida } from '../../Indicacoes/PessoaPicker';
import { CLASSE_BOTAO_PRIMARIO, CLASSE_BOTAO_SECUNDARIO } from '../../Indicacoes/estilos';

/**
 * Programa de Indicações dentro da negociação (#6196):
 * - aviso "Indicado por…" quando a negociação veio de indicação;
 * - selo com o resumo quando o cliente desta negociação já foi abordado;
 * - "Registrar quem indicou" (indicação espontânea) enquanto a negociação está ativa.
 */
const IndicacaoNegociacaoCard: React.FC<{ negociacaoId: number; onChange: () => void }> = ({ negociacaoId, onChange }) => {
  const { profile } = useAuth();
  const { addToast } = useToast();
  const usa = podeUsarIndicacoes(profile);
  const [dados, setDados] = useState<IndicacaoDaNegociacao | null>(null);
  const [abrirModal, setAbrirModal] = useState(false);
  const [pessoa, setPessoa] = useState<PessoaEscolhida | null>(null);
  const [salvando, setSalvando] = useState(false);

  const carregar = useCallback(async () => {
    if (!usa) return;
    try {
      setDados(await indicacaoDaNegociacao(negociacaoId));
    } catch (e) {
      console.error('Erro ao carregar a indicação da negociação:', e);
    }
  }, [negociacaoId, usa]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  if (!usa || !dados) return null;
  const { indicacao, resumo_cliente: resumo, pode_registrar_espontanea: podeRegistrar } = dados;
  const temResumo = !!resumo && (resumo.abordagens > 0 || resumo.indicacoes > 0);
  if (!indicacao && !temResumo && !podeRegistrar) return null;

  const salvar = async () => {
    if (!pessoa) return;
    setSalvando(true);
    try {
      await vincularEspontanea(
        negociacaoId,
        pessoa.id ? { id: pessoa.id } : { nome: pessoa.nome, cpf_cnpj: pessoa.cpf_cnpj, whatsapp: pessoa.whatsapp },
      );
      addToast('Indicação registrada.', 'success');
      setAbrirModal(false);
      setPessoa(null);
      await carregar();
      onChange();
    } catch (e) {
      addToast((e as Error).message, 'error');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="mb-3 space-y-2">
      {indicacao && (
        <div className="rounded-xl border border-emerald-200 dark:border-emerald-800/50 bg-emerald-50 dark:bg-emerald-900/20 px-3 py-2 text-xs text-emerald-800 dark:text-emerald-200">
          <p className="font-semibold flex items-center gap-1.5">
            <Gift size={14} /> {indicacao.forma === 'espontanea' ? 'Indicação espontânea' : 'Veio por indicação'}
          </p>
          <p className="mt-0.5">
            Indicado por <b>{indicacao.indicador_nome}</b> em {formatarDataHora(indicacao.registrado_em)} · registrado por{' '}
            {indicacao.registrado_por_nome ?? '—'}
          </p>
          <Link to={`/crm/indicacoes?pessoa=${indicacao.pessoa_id}`} className="underline font-semibold">
            Ver ficha de quem indicou
          </Link>
        </div>
      )}
      {temResumo && resumo && (
        <div className="rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800/60 px-3 py-2 text-xs text-gray-600 dark:text-slate-300">
          <p className="font-semibold text-gray-800 dark:text-slate-100">Programa de indicações</p>
          <p>{resumoPessoa(resumo)}</p>
        </div>
      )}
      {podeRegistrar && (
        <button
          onClick={() => setAbrirModal(true)}
          className="w-full text-xs font-semibold rounded-lg border border-dashed border-gray-300 dark:border-slate-600 px-3 py-2 text-gray-600 dark:text-slate-300 hover:border-primary hover:text-primary inline-flex items-center justify-center gap-1.5"
        >
          <UserPlus size={14} /> Registrar quem indicou
        </button>
      )}
      {abrirModal && (
        <ModalBase
          titulo="Registrar quem indicou"
          subtitulo="Para quando o cliente chegou dizendo quem o indicou."
          onClose={() => setAbrirModal(false)}
          largura="md"
          rodape={
            <>
              <button onClick={() => setAbrirModal(false)} className={CLASSE_BOTAO_SECUNDARIO} disabled={salvando}>
                Cancelar
              </button>
              <button onClick={salvar} className={CLASSE_BOTAO_PRIMARIO} disabled={salvando || !pessoa}>
                {salvando && <Loader2 size={15} className="animate-spin" />} Registrar
              </button>
            </>
          }
        >
          <PessoaPicker valor={pessoa} onChange={setPessoa} gere={podeGerirIndicacoes(profile)} />
        </ModalBase>
      )}
    </div>
  );
};

export default IndicacaoNegociacaoCard;
